export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

const API_URL = import.meta.env.VITE_API_URL ?? '';

export const SESSION_EXPIRED_EVENT = 'bckt:session-expired';

let refreshRequest: Promise<boolean> | null = null;

async function refreshSession() {
  if (!refreshRequest) {
    refreshRequest = fetch(`${API_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshRequest = null;
      });
  }
  return refreshRequest;
}

async function authenticatedResponse(path: string, init: RequestInit = {}): Promise<Response> {
  const request = () => fetch(`${API_URL}/api/v1${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { 'content-type': 'application/json' }
        : {}),
      ...init.headers,
    },
  });
  let response = await request();
  const refreshable = ![
    '/auth/login',
    '/auth/register',
    '/auth/refresh',
    '/auth/verify-email',
    '/auth/resend-verification',
    '/auth/verification',
    '/auth/forgot-password',
    '/auth/password-reset',
    '/auth/resend-password-reset',
    '/auth/reset-password',
    '/auth/two-factor',
    '/auth/two-factor/verify',
    '/reports',
  ].some((endpoint) => path === endpoint);
  if (response.status === 401 && refreshable && (await refreshSession())) response = await request();
  if (!response.ok) {
    // Detect Cloudflare Managed Challenge / WAF page on background API calls
    const contentType = response.headers.get('content-type') ?? '';
    const isCloudflareChallenge =
      (response.status === 403 || response.status === 503) &&
      (response.headers.get('cf-mitigated') === 'challenge' ||
        contentType.includes('text/html') ||
        response.headers.get('cf-chl-bypass') !== null);

    if (isCloudflareChallenge && typeof window !== 'undefined') {
      // Background fetch cannot solve a Cloudflare Turnstile challenge.
      // Reload the top-level window so Cloudflare presents the verification challenge in the browser.
      window.location.reload();
      // Halt execution while page reloads
      return new Promise(() => {});
    }

    const payload = await response.json().catch(() => null);
    if (
      response.status === 401 &&
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/dashboard')
    ) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    }
    throw new ApiClientError(
      response.status,
      payload?.error?.code ?? 'REQUEST_FAILED',
      payload?.error?.message ?? 'The request failed.',
      payload?.error?.details,
    );
  }
  return response;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await authenticatedResponse(path, init);
  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

export async function downloadApiFile(path: string, filename: string) {
  const response = await authenticatedResponse(path);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url; link.download = filename;
  document.body.appendChild(link);
  link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const formatBytes = (value: number | string) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1000)), units.length - 1);
  return `${(bytes / 1000 ** index).toFixed(index > 2 ? 2 : index > 0 ? 1 : 0)} ${units[index]}`;
};

export const formatMoney = (cents: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 ? 2 : 0,
  }).format(cents / 100);
